import { useQueryClient } from '@tanstack/react-query';
import { assert } from '@varos/util-typescript';
import { BLOCK_LIST_QUERY_KEY } from '../block';
export const CONTENT_LIST_QUERY_KEY = ['research', 'content', 'find'];
export function getContentLookupKey(contentId) {
    return ['research', 'content', 'lookup', { contentId }];
}
export function createContentRepository(config) {
    const { repository, client } = config;
    return {
        useFind(context, props, options) {
            const queryClient = useQueryClient();
            const query = repository.useQuery({
                ...options,
                queryKey: [...CONTENT_LIST_QUERY_KEY, props],
                async queryFn() {
                    if (props.filter) {
                        assert(props.collection, 'collection is required for search');
                        const { data: contents } = await client.user.content.search({
                            contentSearchPayload: {
                                collection: props.collection,
                                filter: props.filter,
                            },
                        });
                        return contents;
                    }
                    const { data: contents } = await client.user.content.list(props);
                    return contents;
                },
                onSuccess(data) {
                    for (const content of data) {
                        const queryKey = getContentLookupKey(content.id);
                        queryClient.setQueryData(queryKey, content);
                    }
                },
            });
            return query;
        },
        useLookup(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getContentLookupKey(props.contentId),
                async queryFn() {
                    const content = await client.user.content.retrieve(props.contentId);
                    return content;
                },
            });
            return query;
        },
        useLookupMaybe(context, id, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getContentLookupKey(id),
                async queryFn() {
                    if (id === null) {
                        return null;
                    }
                    const response = await client.user.content.retrieve(id);
                    return response;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            const mutation = repository.useMutation(context, {
                async mutationFn(payload) {
                    const response = await client.user.content.create(payload);
                    return response;
                },
                async onSuccess(data) {
                    const queryKey = getContentLookupKey(data.id);
                    queryClient.setQueryData(queryKey, data);
                    queryClient.refetchQueries({
                        queryKey: [...CONTENT_LIST_QUERY_KEY],
                    });
                    queryClient.refetchQueries({
                        queryKey: BLOCK_LIST_QUERY_KEY,
                    });
                },
            });
            return mutation;
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            const mutation = repository.useMutation(context, {
                async mutationFn([contentId, payloadWithOptions]) {
                    const { replace, ...payload } = payloadWithOptions;
                    let deleted = [];
                    if (replace) {
                        const currentBlockResponse = await client.user.default.blockList({
                            content: contentId,
                        });
                        deleted = currentBlockResponse.data.data.map((block) => {
                            // @ts-expect-error TODO better typing here
                            return {
                                ...block.data,
                                id: block.id,
                                deleted: true,
                            };
                        });
                        console.log('updating blocks', contentId, {
                            blocks: [...deleted, ...(payload.blocks ?? [])],
                        });
                    }
                    const response = await client.user.content.update(contentId, {
                        ...payload,
                        blocks: [...deleted, ...(payload.blocks ?? [])],
                    });
                    return response;
                },
                async onSuccess(data, [contentId]) {
                    // Update the content in the lookup cache
                    const lookupQueryKey = getContentLookupKey(contentId);
                    queryClient.setQueryData(lookupQueryKey, data);
                    // Refetch related queries
                    await Promise.all([
                        queryClient.refetchQueries({
                            queryKey: [...CONTENT_LIST_QUERY_KEY],
                        }),
                        queryClient.refetchQueries({
                            queryKey: BLOCK_LIST_QUERY_KEY,
                        }),
                    ]);
                },
            });
            return mutation;
        },
        useArchive(context, options = {}) {
            const queryClient = useQueryClient();
            const mutation = repository.useMutation(context, {
                ...options,
                async mutationFn(props) {
                    const data = await client.user.content.archive(props.contentId);
                    await queryClient.refetchQueries({
                        queryKey: [...CONTENT_LIST_QUERY_KEY],
                    });
                    return data;
                },
            });
            return mutation;
        },
    };
}
//# sourceMappingURL=documentRepository.js.map